/* eslint-disable @typescript-eslint/no-namespace */
import { isStaticWidget } from '@vev/utils';
import React, { HTMLAttributes, useEffect, useState } from 'react';
import { IContent } from 'vev';
import { ModelProvider } from '../core/context';
import {
  useEditorState,
  useGlobalStore,
  useModel,
  useRegisterHostRef,
  useVevEvent,
  useVisible,
} from '../core/hooks';
import { useLazyLoadElement } from '../core/hooks/use-lazy-load';
import { isString } from '../utils';
import { Addons } from './addon';
import { CodedComponent } from './coded-component';
import ErrorBounds from './error-bounds';
import { LazyLoadWrapper } from './lazy-load-wrapper';
import { StaticComponent } from './static-component';

declare global {
  namespace JSX {
    interface IntrinsicElements {
      vev: React.DetailedHTMLProps<React.HTMLAttributes<HTMLElement>, HTMLElement>;
      w: React.DetailedHTMLProps<React.HTMLAttributes<HTMLElement>, HTMLElement>;
    }
  }
}

interface WidgetNodeProps {
  /** Model key */
  id: string;
  // model: IContent;
  container?: HTMLDivElement;
  children?: React.ReactNode;
  // hostRef: React.RefObject<HTMLDivElement>;
  className?: string;
  fixed?: boolean;
  section?: boolean;
  externalVariant?: string;
}

function WidgetNode({ id, section, fixed, className, externalVariant }: WidgetNodeProps) {
  const [overrideVariant, setOverrideVariant] = useState<string | undefined>(externalVariant);
  const { disabled } = useEditorState();
  const model = useModel(id, overrideVariant);
  const widgetPkg = useGlobalStore((state) => state.pkg?.[model?.type || ''], [model?.type]);

  const hostRef = React.useRef<HTMLDivElement>(null);
  const modelId = model?.virtualKey || model?.key || id;
  const currentVariant = overrideVariant || model?.variant;
  const doLazyLoad = useLazyLoadElement(model?.type);

  useEffect(() => {
    if (disabled && overrideVariant && !externalVariant) {
      setOverrideVariant(undefined);
    }
  }, [disabled, overrideVariant]);

  useEffect(() => {
    setOverrideVariant(externalVariant);
  }, [externalVariant]);

  useRegisterHostRef(modelId, hostRef);

  useVevEvent<{ variant: string }>(
    'CHANGE_VARIANT',
    ({ variant }) => setOverrideVariant(variant || 'default'),
    hostRef,
  );
  useVevEvent<{ variant: string }>(
    'TOGGLE_VARIANT',
    ({ variant }) => {
      setOverrideVariant(currentVariant === variant ? 'default' : variant);
    },
    hostRef,
  );

  // If not instance of master component, model?.key will be KEY(11)
  // If instance of master component, model?.key will be KEY(11)-MASTERKEY(11)
  // If nested instance of master component, model?.key will be KEY(11)-MASTERKEY(11)-MASTERKEY(11)
  if (!model || !model.type) return null;

  let cl = '__' + model.type + (className ? ' ' + className : '');
  let contentCl = model.type;
  if (widgetPkg) contentCl += ` pkg-${widgetPkg}`;
  if (model.cl) contentCl += ' ' + model.cl;
  if (disabled) contentCl += ' __disabled';

  let childrenCl = '__c';

  const contentProps: HTMLAttributes<HTMLDivElement> = {
    id: modelId + 'c',
    className: contentCl,
  };

  if (isString(model.master)) {
    cl += ` ${model.master}`;
    contentProps.className += ` ${model.master}`;
  } else if (model.master) cl += ` ${modelId}`;

  if (currentVariant && currentVariant !== 'default') {
    contentProps.className += ` ${currentVariant}`;
    cl += ` ${currentVariant}`;
  }

  if (model.fromModel) {
    contentProps.className += ` ${model.fromModel}`;
    cl += ` ${model.fromModel}`;
  }

  if (fixed) cl += ' __f';
  if (section) {
    cl += ' __s';
    childrenCl += ' __sc';
  }

  if (model.type === 'frame' && !section) {
    contentProps.className += ' __wc __c';

    return (
      <ModelProvider value={model}>
        <vev id={modelId} className={cl} ref={hostRef} key={modelId + model.actions?.join() || ''}>
          <Addons addons={model.actions} hostRef={hostRef}>
            {React.createElement(
              model.attrs?.tag || 'div',
              {
                ...contentProps,
                ...(model.attrs || {}),
                tabIndex:
                  model.attrs?.tabIndex !== undefined
                    ? model.attrs.tabIndex
                    : model.hasFocusedState
                      ? 0
                      : undefined,
              },
              <>{model.children?.map((id) => <WidgetNode key={id} id={id} />)}</>,
            )}
          </Addons>
        </vev>
      </ModelProvider>
    );
  }

  const content: React.ReactNode = (
    <w {...contentProps}>
      {doLazyLoad ? (
        <LazyLoadWrapper hostRef={hostRef}>
          <ComponentNode hostRef={hostRef} section={section} model={model} />
        </LazyLoadWrapper>
      ) : (
        <ComponentNode hostRef={hostRef} section={section} model={model} />
      )}
    </w>
  );

  // Need to use create element to get rid of error of not supported jsx element
  return (
    <vev id={modelId} className={cl} ref={hostRef} key={modelId + model.actions?.join() || ''}>
      <ErrorBounds>
        {React.createElement(
          model.attrs?.tag || 'div',
          { className: '__wc', ...(model.attrs || {}) },
          <Addons addons={model.actions} hostRef={hostRef}>
            <ModelProvider value={model}>
              {content &&
                (model.pin ? (
                  <PinContentWrapper hostRef={hostRef}>{content}</PinContentWrapper>
                ) : (
                  content
                ))}
              {(section || (model.children?.length || 0) > 0) && (
                <div className={childrenCl}>
                  {model.children?.map((id) => <WidgetNode key={id} id={id} />)}
                </div>
              )}
            </ModelProvider>
          </Addons>,
        )}
      </ErrorBounds>
    </vev>
  );
}

export default WidgetNode;

function PinContentWrapper({
  hostRef,
  children,
}: {
  hostRef: React.RefObject<HTMLDivElement>;
  children: React.ReactNode;
}) {
  const visible = useVisible(hostRef);
  return <div className={visible ? '__pc' : '__pre__pc'}>{children}</div>;
}

export function ComponentNode({
  model,
  hostRef,
  section,
}: {
  model: IContent;
  hostRef: React.RefObject<HTMLDivElement>;
  section?: boolean;
}) {
  if (!model) return null;

  if (isStaticWidget(model.type as string)) {
    return <StaticComponent model={model} />;
  }
  return <CodedComponent hostRef={hostRef} section={section} model={model} />;
}
